import { lazy, Suspense, useEffect } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router'

import Spinning from './components/Common/Spinner/Spinning'

import { OnlyAuthenticated } from 'components/OnlyAuthenticated'
import { OnlyNOTAuthenticated } from 'components/OnlyNOTAuthenticated'
import { OnlyWithOrganization } from 'components/OnlyWithOrganization'
import AppError from './AppError'
import { Logout } from './features/Auth/Logout'

import HandleAuthentication from 'components/Common/HandleAuthentication'

const ForgotPassword = lazy(() => import('./features/Auth/ForgotPassword'))

const SettingsPage = lazy(
  () => import('./components/OrganizationSettings/SettingsPage'),
)

import { CheckAppVersionRoute } from 'components/CheckAppVersion/CheckAppVersionRoute'
import { NewLoginPage } from 'features/Auth/NewLoginPage'
import GettingStartedPage from 'features/GettingStarted/GettingStartedPage'
import AppPage from 'pages/AppPage/AppPage'
import { NewPartiesPage } from 'pages/SystemManagementPage/ManagementPages/PartiesPages/NewPartiesPage'
import { Debugger } from 'utils/debugger'
import LandingPage from './components/LandingPage/LandingPage'
import Workspaces from './components/Organizations/WorkspacesPage'

const ProjectContainer = lazy(() => import('./pages/ProjectPage/ProjectPage'))

const AcceptInvitePage = lazy(
  () => import('features/CustomerPortal/ConnectedParties/AcceptInvitePage'),
)
const SystemManagementPage = lazy(
  () => import('pages/SystemManagementPage/SystemManagementPage'),
)

const UserProfile = lazy(
  () => import('./components/OrganizationSettings/General/UserProfilePage'),
)

const InviteNotFoundPage = lazy(
  () =>
    import('features/CustomerPortal/ConnectedParties/InvitationNotFoundPage'),
)

const QuotationExchangePage = lazy(
  () => import('./features/RequestForQuotations/QuotationExchangePage'),
)

const WithSuspence = (props: React.PropsWithChildren<{ routeKey: string }>) => {
  return (
    <Suspense key={props.routeKey} fallback={<Spinning />}>
      {props.children}
    </Suspense>
  )
}

export const AppRoutes = () => {
  const location = useLocation()
  // const navigate = useNavigate()

  useEffect(() => {
    // checkAppVersion(navigate)

    window.parent?.postMessage(
      {
        type: 'RH24_EMBEDDED_LOCATION_CHANGE',
        payload: { ...location },
      },
      '*',
    )
  }, [location])

  Debugger.Info('routes: AppRoutes', {
    location,
  })

  return (
    <Suspense fallback={<Spinning />}>
      <Routes>
        <Route
          path="/"
          element={
            <WithSuspence routeKey="CheckAppVersionRoute">
              <CheckAppVersionRoute />
            </WithSuspence>
          }
        >
          <Route
            path="/auth/callback/*"
            element={
              <WithSuspence routeKey="HandleAuthentication">
                <HandleAuthentication />
              </WithSuspence>
            }
          />
          <Route
            path="/app/acceptinvite"
            element={
              <WithSuspence routeKey="AcceptInvitePage">
                <AcceptInvitePage />
              </WithSuspence>
            }
          />
          <Route
            path="/getting-started/*"
            element={
              <WithSuspence routeKey="GettingStartedPage">
                <GettingStartedPage />
              </WithSuspence>
            }
          />
          {/* ONLY AUTHENTICATED ROUTES */}
          <Route
            path="/app"
            element={
              <WithSuspence routeKey="OnlyAuthenticated">
                <OnlyAuthenticated />
              </WithSuspence>
            }
          >
            {/* ONLY ROUTES WITH ORGANIZATION_ID SETTED */}
            <Route
              path=""
              element={
                <WithSuspence routeKey="OnlyWithOrganization">
                  <OnlyWithOrganization />
                </WithSuspence>
              }
            >
              <Route
                index
                element={
                  <WithSuspence routeKey="AppPage">
                    <AppPage />
                  </WithSuspence>
                }
              />
              <Route
                path="settings/*"
                element={
                  <WithSuspence routeKey="SettingsPage">
                    <SettingsPage />
                  </WithSuspence>
                }
              />
              <Route
                path="project/:projectId/details?/:bomItemType?/:bomItemId?/:tab?"
                element={
                  <WithSuspence routeKey="ProjectContainer">
                    <ProjectContainer />
                  </WithSuspence>
                }
              />
              <Route
                path="request-for-quotations/incoming"
                element={
                  <WithSuspence routeKey="IncomingRfqs">
                    <QuotationExchangePage direction="incoming-rfqs" />
                  </WithSuspence>
                }
              />
              <Route
                path="request-for-quotations/outgoing"
                element={
                  <WithSuspence routeKey="OutgoingRfqs">
                    <QuotationExchangePage direction="outgoing-rfqs" />
                  </WithSuspence>
                }
              />
              <Route
                path="quotations/incoming"
                element={
                  <WithSuspence routeKey="IncomingQuotations">
                    <QuotationExchangePage direction="incoming-quotations" />
                  </WithSuspence>
                }
              />
              <Route
                path="quotations/outgoing"
                element={
                  <WithSuspence routeKey="OutgoingQuotations">
                    <QuotationExchangePage direction="outgoing-quotations" />
                  </WithSuspence>
                }
              />
              <Route
                path="project/:projectId/bomItem/:bomItemId/:component?"
                element={
                  <WithSuspence routeKey="ProjectContainer">
                    <ProjectContainer />
                  </WithSuspence>
                }
              />
              <Route
                path="system-management"
                element={
                  <WithSuspence routeKey="SystemManagementPage">
                    <SystemManagementPage />
                  </WithSuspence>
                }
              >
                <Route
                  index
                  element={
                    <WithSuspence routeKey="h3">
                      <h3>System Management</h3>
                    </WithSuspence>
                  }
                />
                <Route
                  path="parties"
                  element={
                    <WithSuspence routeKey="NewPartiesPage">
                      <NewPartiesPage />
                    </WithSuspence>
                  }
                />
              </Route>
            </Route>
            {/* END --- ONLY ROUTES WITH ORGANIZATION_ID SETTED */}

            <Route
              path="workspaces"
              element={
                <WithSuspence routeKey="Workspaces">
                  <Workspaces />
                </WithSuspence>
              }
            />
            <Route
              path="invite-not-found"
              element={
                <WithSuspence routeKey="InviteNotFoundPage">
                  <InviteNotFoundPage />
                </WithSuspence>
              }
            />

            <Route
              path="user"
              element={
                <WithSuspence routeKey="UserProfile">
                  <UserProfile containerMaxWidth={'lg'} />
                </WithSuspence>
              }
            />
          </Route>
          {/* END --- ONLY AUTHENTICATED ROUTES */}

          {/* ONLY NOT AUTHENTICATED ROUTES */}
          <Route
            element={
              <WithSuspence routeKey="OnlyNOTAuthenticated">
                <OnlyNOTAuthenticated />
              </WithSuspence>
            }
          >
            {/* <Route path="/login" element={
            <WithSuspence key="LoginWithErrorPage">
            <LoginWithErrorPage /></WithSuspence>} /> */}
            <Route
              path="/login"
              element={
                <WithSuspence routeKey="NewLoginPage">
                  <NewLoginPage />
                </WithSuspence>
              }
            />
          </Route>
          {/* END --- ONLY NOT AUTHENTICATED ROUTES */}

          <Route
            path="/"
            element={
              <WithSuspence routeKey="LandingPage">
                <LandingPage />
              </WithSuspence>
            }
          />
          <Route
            path="/app/error"
            element={
              <WithSuspence routeKey="AppError">
                <AppError />
              </WithSuspence>
            }
          />

          {/* <Route path="/auth/callback/*" element={
          <WithSuspence key="HandleAuthentication">
          <HandleAuthentication /></WithSuspence>} /> */}
          <Route
            path="/logout"
            element={
              <WithSuspence routeKey="Logout">
                <Logout />
              </WithSuspence>
            }
          />

          <Route
            path="/auth/forgot-password"
            element={
              <WithSuspence routeKey="ForgotPassword">
                <ForgotPassword />
              </WithSuspence>
            }
          />

          <Route
            path="*"
            element={
              <Navigate
                replace
                to="/"
                state={{
                  returnUrl:
                    location.pathname + location.search + location.hash,
                }}
              />
            }
          />
        </Route>
      </Routes>
    </Suspense>
  )
}

export default AppRoutes
